import { HttpClient } from '@angular/common/http';
import { effect, inject, Injectable, Injector, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { lastValueFrom, Observable, shareReplay } from 'rxjs';
import {
  EmployeePartial,
  IAllCertificates,
  IAllSkills,
  IEmployee,
  IEmployeeNameEmail,
  IEmployeeProfile,
  IEmployeeSearchRequest,
  IEmployeeSearchResponse,
  IEmployeeSimple,
  IPosition,
  SearchEmployersParams,
} from '../models/';
import { IManagerSubordinatesModel } from '../models/manager-subordinates.model';
import { IPagination } from '../models/pagination.model';
import { ProjectHistory } from '../models/project-history.model';
import { EmployeeProject } from '../models/project.model';
import { EmployeeTimelineEvent } from '../models/employee-timeline.model';
import { AlertService, AlertType } from './alert.service';

const filtersMappingGuide = [
  {
    propertiesToMap: ['ids'],
    term: 'certificates',
  },
  {
    propertiesToMap: ['ids'],
    term: 'position',
  },
  {
    propertiesToMap: ['optionalIds', 'requiredIds'],
    term: 'skills',
  },
];

@Injectable({
  providedIn: 'root',
})
export class EmployeeService {
  private alertService = inject(AlertService);
  private httpClient = inject(HttpClient);
  private injector = inject(Injector);

  private readonly urlBase: string = 'api/employees';
  private allEmployeesSimple$: Observable<IEmployeeSimple[]>;
  private allEmployeesSimpleInclInactive$: Observable<IEmployeeSimple[]>;
  private allEmployeesProfiles$: Observable<IEmployeeProfile[]>;

  private _employees = signal<IPagination<IEmployeeSearchResponse>>(undefined);
  readonly employees = this._employees.asReadonly();

  public uploadImg(id: number, file: File): Observable<string> {
    const formData: FormData = new FormData();
    formData.append('file', file);
    return this.httpClient.post<string>(`${this.urlBase}/${id}/photo`, formData);
  }

  public deleteImg(id: number): Observable<void> {
    return this.httpClient.delete<void>(`${this.urlBase}/${id}/photo`);
  }

  searchEmployees({
    params,
    payload,
  }: SearchEmployersParams): Observable<IPagination<IEmployeeSearchResponse>> {
    return this.httpClient.post<IPagination<IEmployeeSearchResponse>>(
      `${this.urlBase}/search`,
      payload,
      {
        params,
      },
    );
  }

  fetchEmployees({ params, payload }: SearchEmployersParams) {
    const responseSignal = toSignal(this.searchEmployees({ params, payload }), {
      injector: this.injector,
    });

    effect(
      () => {
        const data = responseSignal();
        if (!data) {
          this._employees.set(undefined);
          return;
        }
        try {
          this._employees.set(data);
        } catch (error) {
          this.alertService.showAlert(error, AlertType.error);
        }
      },
      { injector: this.injector, allowSignalWrites: true },
    );
  }

  getAllEmployees(): Observable<IEmployee[]> {
    return this.httpClient.get<IEmployee[]>(this.urlBase);
  }

  getAllEmployeesSimple(): Observable<IEmployeeSimple[]> {
    this.allEmployeesSimple$ =
      this.allEmployeesSimple$ ?? this.fetchAllEmployeesSimple().pipe(shareReplay());
    return this.allEmployeesSimple$;
  }

  getAllEmployeesSimpleIncludingInactive(): Observable<IEmployeeSimple[]> {
    this.allEmployeesSimpleInclInactive$ =
      this.allEmployeesSimpleInclInactive$ ??
      this.fetchAllEmployeesSimpleIncludingInactive().pipe(shareReplay());
    return this.allEmployeesSimpleInclInactive$;
  }

  getAllEmployeesProfiles(): Observable<IEmployeeProfile[]> {
    this.allEmployeesProfiles$ =
      this.allEmployeesProfiles$ ??
      this.httpClient.get<IEmployeeProfile[]>(`${this.urlBase}/profile`).pipe(shareReplay());
    return this.allEmployeesProfiles$;
  }

  fetchAllEmployeesProfiles(): Observable<IEmployeeProfile[]> {
    return this.httpClient.get<IEmployeeProfile[]>(`${this.urlBase}/profile`);
  }

  getAllEmployeesNameEmail(): Observable<IEmployeeNameEmail[]> {
    return this.httpClient.get<IEmployeeNameEmail[]>(`${this.urlBase}/name-email`);
  }

  getActiveEmployeesByProjectId(projectId: number): Observable<IEmployee[]> {
    return this.httpClient.get<IEmployee[]>(`${this.urlBase}/by-project/${projectId}`);
  }

  async loadMyProjects(id: number) {
    return await lastValueFrom(
      this.httpClient.get<Array<EmployeeProject>>(`${this.urlBase}/${id}/projects`),
    );
  }

  getProjectHistoryByEmployeeId(employeeId: number): Observable<ProjectHistory[]> {
    return this.httpClient.get<ProjectHistory[]>(`${this.urlBase}/${employeeId}/projects-history`);
  }

  getTimeline(employeeId: number): Observable<EmployeeTimelineEvent[]> {
    return this.httpClient.get<EmployeeTimelineEvent[]>(`${this.urlBase}/${employeeId}/timeline`);
  }

  getPartialEmployees(): Observable<EmployeePartial[]> {
    return this.httpClient.get<EmployeePartial[]>(`${this.urlBase}/partial`);
  }

  editEmployee(employee: IEmployee): Observable<IEmployee> {
    return this.httpClient.put<IEmployee>(`${this.urlBase}`, employee);
  }

  changePassword({ email }: { email: string }) {
    return this.httpClient.post<Boolean>(`${this.urlBase}/password/change`, { email });
  }

  getEmployeeById(id: number): Observable<IEmployee> {
    return this.httpClient.get<IEmployee>(`${this.urlBase}/${id}`);
  }

  getEmployeePicture(userId: number): Observable<any> {
    return this.httpClient.get(`${this.urlBase}/${userId}/photo/${Date.now()}.jpg`, {
      responseType: 'blob',
    });
  }

  getAllManagerSubordinates(managerId: number): Observable<IManagerSubordinatesModel[]> {
    return this.httpClient.get<IManagerSubordinatesModel[]>(
      `${this.urlBase}/getManagerSubordinates/${managerId}`,
    );
  }

  getOrganizationRoots(): Observable<IManagerSubordinatesModel[]> {
    return this.httpClient.get<IManagerSubordinatesModel[]>(`${this.urlBase}/organization`);
  }

  filterEmployeesByName(value: string, list: IEmployeeSimple[]): IEmployeeSimple[] {
    if (!value) return list.slice();
    if (typeof value !== 'string') return list.filter((emp) => emp !== value);

    const filterValue = value.toLowerCase();
    return list.filter((emp) => emp.fullName.toLowerCase().includes(filterValue));
  }

  mapFormValueToRequest(formValue: IEmployeeSearchRequest): IEmployeeSearchRequest {
    if (formValue) {
      const mappedValue = {} as IEmployeeSearchRequest;

      filtersMappingGuide.forEach(({ term, propertiesToMap }) => {
        if (formValue[term]) {
          mappedValue[term] = { ...formValue[term] };

          propertiesToMap?.forEach((propertyToMap) => {
            // map the array of objects to an array of ids
            mappedValue[term][propertyToMap] = mappedValue[term][propertyToMap]?.map(
              (item: IAllSkills | IAllCertificates | IPosition) => item.id,
            );
          });

          // check if all arrays are empty so we can remove term
          const isEmpty = propertiesToMap
            .map((propertyToMap) => !(mappedValue[term][propertyToMap]?.length > 0))
            .every(Boolean);

          if (isEmpty) delete mappedValue[term];
        }
      });

      // search
      if (formValue.search) {
        mappedValue.search = formValue.search;
      }

      return Object.keys(mappedValue).length > 0 ? mappedValue : null;
    }

    return null;
  }

  private fetchAllEmployeesSimple(): Observable<IEmployeeSimple[]> {
    return this.httpClient.get<IEmployeeSimple[]>(`${this.urlBase}/simple`);
  }

  private fetchAllEmployeesSimpleIncludingInactive(): Observable<IEmployeeSimple[]> {
    return this.httpClient.get<IEmployeeSimple[]>(`${this.urlBase}/simple/all`);
  }
}
